/*
 *
 * TargetingPage constants
 *
 */

import { sortBy, mapValues, keyBy, union, flatMap } from 'lodash';
import {
  USER_RESEARCHER,
  USER_ADMIN,
  USER_PUBLISHER,
} from 'containers/User/constants';
import {
  SURVEY_TYPE_REGULAR,
  SURVEY_TYPE_THIRD_PARTY,
  SURVEY_TYPE_INTERNAL,
} from 'containers/Survey/constants';
import { HELP_TOOLTIP_TYPES } from 'components/HelpTooltip/constants';
import { getSurveyLocationResetExceptionRules } from 'containers/SurveyPage/constants';

const scope = 'app/TargetingPage';

export const ADD_TARGETING_CRITERIA = `${scope}/ADD_TARGETING_CRITERIA`;
export const REMOVE_TARGETING_CRITERIA = `${scope}/REMOVE_TARGETING_CRITERIA`;
export const UPDATE_TARGETING = `${scope}/UPDATE_TARGETING`;
export const STORE_TARGETING_SUCCESS = `${scope}/STORE_TARGETING_SUCCESS`;
export const FETCH_TARGETING = `${scope}/FETCH_TARGETING`;
export const FETCH_TARGETING_SUCCESS = `${scope}/FETCH_TARGETING_SUCCESS`;
export const FETCH_TARGETING_ERROR = `${scope}/FETCH_TARGETING_ERROR`;
export const ADD_SCREENING_QUESTION = `${scope}/ADD_SCREENING_QUESTION`;
export const DUPLICATE_MULTIPLE_SCREENING_QUESTIONS = `${scope}/DUPLICATE_MULTIPLE_SCREENING_QUESTIONS`;
export const REMOVE_SCREENING_QUESTION = `${scope}/REMOVE_SCREENING_QUESTION`;
export const REMOVE_ALL_SCREENING_QUESTIONS = `${scope}/REMOVE_ALL_SCREENING_QUESTIONS`;
export const UPDATE_SCREENING_QUESTION = `${scope}/UPDATE_SCREENING_QUESTION`;
export const DISABLE_SCREENING_QUESTION = `${scope}/DISABLE_SCREENING_QUESTION`;
export const DISABLE_ALL_SCREENING_QUESTIONS = `${scope}/DISABLE_ALL_SCREENING_QUESTIONS`;

export const UPDATE_VALIDATION_ERRORS = `${scope}/UPDATE_VALIDATION_ERRORS`;

export const SCROLL_TO_FEATURE = `${scope}/SCROLL_TO_FEATURE`;
export const SHOW_ALL_ERRORS = `${scope}/SHOW_ALL_ERRORS`;

export const DELETE_MEDIA = `${scope}/DELETE_MEDIA`;

export const RESUME_SURVEY = `${scope}/RESUME_SURVEY`;
export const RESUME_SURVEY_SUCCESS = `${scope}/RESUME_SURVEY_SUCCESS`;
export const RESUME_SURVEY_ERROR = `${scope}/RESUME_SURVEY_ERROR`;

export const SET_ACTIVE_AUDIENCE = `${scope}/SET_ACTIVE_AUDIENCE`;
export const ADD_AUDIENCE = `${scope}/ADD_AUDIENCE`;
export const REMOVE_AUDIENCE = `${scope}/REMOVE_AUDIENCE`;
export const DUPLICATE_AUDIENCE = `${scope}/DUPLICATE_AUDIENCE`;
export const UPDATE_AUDIENCE = `${scope}/UPDATE_AUDIENCE`;
export const SCREENING_QUESTIONS_CRITERION_KEY = 'sq_all';
export const residentialPostalCodesCriterionKey = 'gpc_place_id';
export const residentialPostalCodesResultsCriterionKey = 'has_gpc_post_code';

export const screeningQuestionsCriteria = {
  key: SCREENING_QUESTIONS_CRITERION_KEY,
  label: 'Screening Questions',
  order: 21,
  isScreening: true,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
  helpTooltip: HELP_TOOLTIP_TYPES.SCREENING,
};

const languagerCriterion = {
  key: 'language',
  label: 'Survey Language',
  order: 5,
  isDisablingAllowed: false,
  allowedUserTypes: [USER_RESEARCHER, USER_ADMIN, USER_PUBLISHER],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const genderCriterion = {
  key: 'gender',
  label: 'Gender',
  order: 10,
  isDisablingAllowed: false,
  allowedUserTypes: [USER_RESEARCHER, USER_ADMIN, USER_PUBLISHER],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

export const ageCriterion = {
  key: 'age',
  featureKey: 'Age',
  label: 'Age Range',
  order: 20,
  isDisablingAllowed: false,
  allowedUserTypes: [USER_RESEARCHER, USER_ADMIN, USER_PUBLISHER],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const residentialPostalCodesCriterion = {
  key: residentialPostalCodesCriterionKey,
  label: 'Residential Postal Codes',
  order: 35,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
  // isNewFeature: true,
};

const countryCriterion = {
  key: 'country',
  label: 'Country',
  order: 29,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const regionCriterion = {
  key: 'region',
  label: 'Region / State',
  order: 30,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const countyCriterion = {
  key: 'county_surrogate_id',
  label: 'County',
  order: 31,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const cityCriterion = {
  key: 'city',
  label: 'City',
  order: 32,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const radiusCriterion = {
  key: 'latlon',
  label: 'Radius',
  order: 33,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const postalCodeCriterion = {
  key: 'postal_code',
  label: 'US Postal code',
  order: 51,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const usCensusRegionCriterion = {
  key: 'us_census_region',
  label: 'US Census Region',
  order: 35,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const usCensusDivisionCriterion = {
  key: 'us_census_division',
  label: 'US Census Division',
  order: 36,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const usCongressionalDistrictCriterion = {
  key: 'us_congressional_district',
  label: 'US Congressional District',
  order: 38,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const dmaCodeCriterion = {
  key: 'dma_code',
  label: 'US DMA®',
  order: 37,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const maritalStatusCriterion = {
  key: 'marital_status',
  label: 'Marital Status',
  order: 90,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const parentalCriterion = {
  key: 'dg_parental',
  label: 'Number of Children',
  order: 100,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const educationCriterion = {
  key: 'dg_education',
  label: 'Education',
  order: 110,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const employmentCriterion = {
  key: 'dg_employment',
  label: 'Employment',
  order: 120,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const careerCriterion = {
  key: 'dg_career',
  label: 'Career',
  order: 130,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const raceCriterion = {
  key: 'dg_race',
  label: 'Ethnicity',
  order: 140,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const incomeCriterion = {
  key: 'dg_income',
  label: 'Household Income',
  order: 150,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const osCriterion = {
  key: 'os',
  label: 'OS Platform',
  order: 160,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const manufacturerCriterion = {
  key: 'manufacturer',
  label: 'Manufacturer',
  order: 170,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const residentialPostalCodesResultsCriterion = {
  key: residentialPostalCodesResultsCriterionKey,
  label: 'Residential Postal Codes',
  order: 184,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const mobileCarrierCriterion = {
  key: 'mobile_carrier',
  label: 'Mobile Carrier',
  order: 185,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

// New Criterion currently disabled
// const speakingLanguagesCriterion = {
//   key: 'dg_speaking_langs',
//   label: 'Spoken Languages',
//   order: 186,
//   isDisablingAllowed: true,
// };

const numberOfEmployeesCriterion = {
  key: 'dg_number_of_employees',
  label: 'Number of Employees',
  order: 187,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

const organizationRoleCriterion = {
  key: 'dg_organization_role',
  label: 'Organization Role',
  order: 188,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_PUBLISHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
};

// const personasCriterion = {
//   key: 'personas',
//   label: 'Persona',
//   order: 80,
// };
// const appsCriterion = {
//   key: 'apps',
//   label: 'Installed App',
//   order: 155,
// };
// const latlonCriterion = {
//   key: 'latlon',
//   label: 'Latitude/Longitude',
//   order: 190,
// };

// api keys on pollfish_regular/thirdparty surveys can only be entered by text
const apiKeyCriterion = {
  key: 'api_key',
  label: 'API key',
  order: 190,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_ADMIN, USER_PUBLISHER],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_THIRD_PARTY,
    SURVEY_TYPE_INTERNAL,
  ],
  isRequired: false, // isRequired true on internal only
};

const deviceIdCriterion = {
  key: 'device_id',
  label: 'Device id',
  order: 191,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_INTERNAL,
    SURVEY_TYPE_THIRD_PARTY,
  ],
};

const developerIdCriterion = {
  key: 'developer_id',
  label: 'Developer id',
  order: 192,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_INTERNAL,
    SURVEY_TYPE_THIRD_PARTY,
  ],
};

const advertisingIdCriterion = {
  key: 'has_advertising_id',
  label: 'Advertising id',
  order: 189,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_ADMIN],
  allowedSurveyTypes: [
    SURVEY_TYPE_REGULAR,
    SURVEY_TYPE_INTERNAL,
    SURVEY_TYPE_THIRD_PARTY,
  ],
};

const mobileOsVersionCriterion = {
  key: 'has_os_version',
  label: 'Mobile OS Version (iOS & Android)',
  order: 186,
  isDisablingAllowed: true,
  allowedUserTypes: [USER_RESEARCHER, USER_ADMIN],
  allowedSurveyTypes: [SURVEY_TYPE_REGULAR, SURVEY_TYPE_THIRD_PARTY],
};

export const deviceCategory = 'device_usage';
export const advertisingItemKey = 'advertising_criteria';
export const geolocationItemKey = 'geolocation_criteria';
export const targetingItemKey = 'targeting_criteria';
export const publisherTargetingCategory = 'targeting';
export const screeningItemKey = 'screening';

export const initialCriteria = sortBy(
  [
    genderCriterion,
    ageCriterion,
    residentialPostalCodesCriterion,
    countryCriterion,
    regionCriterion,
    countyCriterion,
    cityCriterion,
    usCensusRegionCriterion,
    dmaCodeCriterion,
    usCensusDivisionCriterion,
    usCongressionalDistrictCriterion,
    postalCodeCriterion,
    radiusCriterion,
    careerCriterion,
    educationCriterion,
    employmentCriterion,
    maritalStatusCriterion,
    parentalCriterion,
    incomeCriterion,
    raceCriterion,
    residentialPostalCodesResultsCriterion,
    mobileCarrierCriterion,
    osCriterion,
    manufacturerCriterion,
    numberOfEmployeesCriterion,
    organizationRoleCriterion,
    apiKeyCriterion,
    deviceIdCriterion,
    developerIdCriterion,
    advertisingIdCriterion,
    mobileOsVersionCriterion,
  ],
  'order',
);

export const initialState = {
  isSaving: false,
  isFetching: false,
  hasFetched: false,
  targetingEditVersion: -1,
  screeningQuestions: [],
  errors: [],
  comments: {},
  criteria: sortBy(
    [
      genderCriterion,
      ageCriterion,
      residentialPostalCodesCriterion,
      countryCriterion,
      regionCriterion,
      countyCriterion,
      cityCriterion,
      usCensusRegionCriterion,
      dmaCodeCriterion,
      usCensusDivisionCriterion,
      usCongressionalDistrictCriterion,
      postalCodeCriterion,
      radiusCriterion,
      careerCriterion,
      educationCriterion,
      employmentCriterion,
      maritalStatusCriterion,
      parentalCriterion,
      incomeCriterion,
      raceCriterion,
      mobileCarrierCriterion,
      osCriterion,
      manufacturerCriterion,
      numberOfEmployeesCriterion,
      organizationRoleCriterion,
      advertisingIdCriterion,
      apiKeyCriterion,
      deviceIdCriterion,
      developerIdCriterion,
      mobileOsVersionCriterion,
    ],
    'order',
  ),
  audiences: [
    {
      refId: '',
      criteria: initialCriteria,
    },
  ],
  activeAudienceRefId: null,
};

const geolocationMenuItems = {
  key: geolocationItemKey,
  label: 'Geolocation criteria',
  criteria: sortBy(
    [
      countryCriterion,
      regionCriterion,
      countyCriterion,
      cityCriterion,
      radiusCriterion,
      residentialPostalCodesCriterion,
      usCensusRegionCriterion,
      usCensusDivisionCriterion,
      dmaCodeCriterion,
      usCongressionalDistrictCriterion,
      postalCodeCriterion,
    ],
    'order',
  ),
};

export const geolocationCriteriaKeysWithoutPostalCode =
  geolocationMenuItems.criteria.reduce((accumulator, criterion) => {
    if (criterion.key === residentialPostalCodesCriterionKey) {
      return accumulator;
    }

    return [...accumulator, criterion.key];
  }, []);

const allMenuItems = [
  {
    label: 'Preselected',
    criteria: sortBy(
      [languagerCriterion, genderCriterion, ageCriterion],
      'order',
    ),
  },
  {
    key: screeningItemKey,
    label: 'Screening Questions',
    // criteria: sortBy([sq0Criterion, sq1Criterion, sq2Criterion], 'order'),
    criteria: [screeningQuestionsCriteria],
    hideListHeader: true,
  },
  geolocationMenuItems,
  {
    label: 'Demographic criteria',
    criteria: sortBy(
      [
        careerCriterion,
        educationCriterion,
        employmentCriterion,
        maritalStatusCriterion,
        parentalCriterion,
        incomeCriterion,
        raceCriterion,
        numberOfEmployeesCriterion,
        organizationRoleCriterion,
      ],
      'order',
    ),
  },
  {
    key: targetingItemKey,
    label: 'Targeting info at exports',
    criteria: [residentialPostalCodesResultsCriterion],
  },
  {
    key: deviceCategory,
    label: 'Mobile usage criteria',
    criteria: sortBy(
      [
        mobileCarrierCriterion,
        mobileOsVersionCriterion,
        osCriterion,
        manufacturerCriterion,
      ],
      'order',
    ),
  },
  {
    key: advertisingItemKey,
    label: 'Advertising',
    criteria: [advertisingIdCriterion],
  },
  {
    key: publisherTargetingCategory,
    label: 'Targeting',
    criteria: [apiKeyCriterion],
    allowedSurveyTypes: [SURVEY_TYPE_INTERNAL],
  },
  {
    label: 'Pollfish Users',
    criteria: sortBy(
      [apiKeyCriterion, deviceIdCriterion, developerIdCriterion],
      'order',
    ),
    allowedUserTypes: [USER_ADMIN],
    allowedSurveyTypes: [
      SURVEY_TYPE_REGULAR,
      SURVEY_TYPE_INTERNAL,
      SURVEY_TYPE_THIRD_PARTY,
    ],
  },
];

// aggregate allowedUserTypes by criterion for each category as well
// to help with some renders
export const menuItems = allMenuItems.map((category) => {
  let aggregatedAllowedUserTypes = [];
  let aggregatedAllowedSurveyTypes = [];
  category.criteria.forEach((criterion) => {
    aggregatedAllowedUserTypes = union(
      aggregatedAllowedUserTypes,
      criterion.allowedUserTypes,
    );
    aggregatedAllowedSurveyTypes = union(
      aggregatedAllowedSurveyTypes,
      criterion.allowedSurveyTypes,
    );
  });
  return {
    ...category,
    // only append if no value was set beforehand
    allowedUserTypes: category.allowedUserTypes
      ? category.allowedUserTypes
      : aggregatedAllowedUserTypes,
    allowedSurveyTypes: category.allowedSurveyTypes
      ? category.allowedSurveyTypes
      : aggregatedAllowedSurveyTypes,
  };
});

export const allMenuCriteriaOrder = mapValues(
  keyBy(flatMap(menuItems, 'criteria'), 'key'),
  'order',
);

export const modalActions = {
  REMOVE_SCREENING_QUESTION: 'REMOVE_SCREENING_QUESTION',
  REMOVE_ALL_SCREENING_QUESTIONS: 'REMOVE_ALL_SCREENING_QUESTIONS',
  DISABLE_SCREENING_QUESTION: 'DISABLE_SCREENING_QUESTION',
  DISABLE_ALL_SCREENING_QUESTIONS: 'DISABLE_ALL_SCREENING_QUESTIONS',
  RESUME_SURVEY: 'RESUME_SURVEY',
  DISCARD_SURVEY: 'DISCARD_SURVEY',
  REMAINING_COMPLETES_DISTRIBUTION: 'REMAINING_COMPLETES_DISTRIBUTION',
  REMOVE_CRITERION: 'REMOVE_CRITERION',
  REMOVE_CRITERION_VALUE: 'REMOVE_CRITERION_VALUE',
  ENABLE_RESIDENTIAL_POSTAL_CODES: 'ENABLE_RESIDENTIAL_POSTAL_CODES',
};

export const SURVEY_TYPE_ENUM = {
  REGULAR: 0,
  INTERNAL: 4,
  THIRD_PARTY: 6,
};

export const BOOLEAN_CRITERIA = [
  'has_advertising_id',
  'has_os_version',
  residentialPostalCodesResultsCriterionKey,
];

export const COMMON_AUDIENCE_CRITERIA = [
  ...BOOLEAN_CRITERIA,
  'developer_id',
  'device_id',
];
export const NEW_AUDIENCE_COMPLETES = 600;
export const MIN_AUDIENCE_COMPLETES = 1;
export const MIN_SURVEY_COMPLETES = 100;
export const MIN_SURVEY_THIRD_PARTY_COMPLETES = 400;
export const MAX_SURVEY_COMPLETES = 20000;

export const locationResetExceptionRoutes = [
  ...getSurveyLocationResetExceptionRules(),
];
